
import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { Account } from "@/types/strategy-types";

interface AccountsTableContextType {
  isCreateDialogOpen: boolean;
  setIsCreateDialogOpen: (isOpen: boolean) => void;
  editingAccount: Account | null;
  setEditingAccount: (account: Account | null) => void;
  deletingAccount: Account | null;
  setDeletingAccount: (account: Account | null) => void;
}

const AccountsTableContext = createContext<AccountsTableContextType>({
  isCreateDialogOpen: false,
  setIsCreateDialogOpen: () => {},
  editingAccount: null,
  setEditingAccount: () => {},
  deletingAccount: null,
  setDeletingAccount: () => {},
});

export const useAccountsTable = () => useContext(AccountsTableContext);

export const AccountsTableProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState<Account | null>(null);
  const [deletingAccount, setDeletingAccount] = useState<Account | null>(null);

  const handleOpenCreateDialog = useCallback(() => {
    setIsCreateDialogOpen(true);
  }, []);

  const handleOpenEditDialog = useCallback((event: CustomEvent) => {
    const account = event.detail?.account;
    if (account) {
      setEditingAccount(account);
    }
  }, []);

  const handleOpenDeleteDialog = useCallback((event: CustomEvent) => {
    const account = event.detail?.account;
    if (account) {
      setDeletingAccount(account);
    }
  }, []);

  useEffect(() => {
    window.addEventListener('openCreateAccountDialog', handleOpenCreateDialog);
    window.addEventListener('openEditAccountDialog', handleOpenEditDialog as EventListener);
    window.addEventListener('openDeleteAccountDialog', handleOpenDeleteDialog as EventListener);
    
    return () => {
      window.removeEventListener('openCreateAccountDialog', handleOpenCreateDialog);
      window.removeEventListener('openEditAccountDialog', handleOpenEditDialog as EventListener);
      window.removeEventListener('openDeleteAccountDialog', handleOpenDeleteDialog as EventListener);
    };
  }, [handleOpenCreateDialog, handleOpenEditDialog, handleOpenDeleteDialog]);

  return (
    <AccountsTableContext.Provider value={{
      isCreateDialogOpen,
      setIsCreateDialogOpen,
      editingAccount,
      setEditingAccount,
      deletingAccount,
      setDeletingAccount,
    }}>
      {children}
    </AccountsTableContext.Provider>
  );
};
